import { makeAutoObservable, runInAction } from 'mobx'
import RootStore from '../../../../stores/RootStore'

export interface InvoiceFormData {
  purchaseOrderId?: string
  customerId?: string
  poNumber?: string
  invoiceNumber?: string
  invoiceDate?: Date
  dueDate?: Date
  paymentTerms?: number
  taxRate?: number
  notes?: string
  billingAddress?: string
  shippingAddress?: string
  totalAmount?: number
  currency?: string
}

export interface ConversionCheck {
  canConvert: boolean
  reason?: string
  po?: any // The PO data if found
}

export class POConversionViewStore {
  // Observables - UI state only
  currentStep = 0
  selectedPOId: string | null = null
  invoiceFormData: Partial<InvoiceFormData> = {}
  selectedApproverIds: string[] = []
  validationErrors: Map<string, string> = new Map()
  isDirty = false
  isSaving = false
  isSubmitting = false
  conversionCheckResult: ConversionCheck | null = null
  serviceHasError = false
  errorMessage: string | null = null
  isCheckingConversion = false
  selectedLineItemIds: string[] = []
  conversionSuccess = false


  constructor(private rootStore: RootStore) {
    makeAutoObservable(this, { rootStore: false } as any)
  }

  // Actions
  setCurrentStep = (step: number) => {
    this.currentStep = step
  }

  selectPurchaseOrder = (poId: string) => {
    this.selectedPOId = poId
    this.isDirty = true

    // Pre-populate invoice data from PO
    // Find the PO directly from the store
    const po = (this.rootStore as any).purchaseOrdersStore.entities.find((p: any) =>
      p.purchaseOrderId === poId || p.id === poId
    )

    if (po) {
      runInAction(() => {
        this.invoiceFormData = {
          purchaseOrderId: po.purchaseOrderId || po.id,
          customerId: po.accountId || po.customerId || po.supplierId, // Check multiple possible field names
          poNumber: po.poNumber,
          billingAddress: po.billingAddress || '',
          shippingAddress: po.shippingAddress || '',
          notes: po.notes || '',
          totalAmount: po.totalAmount || 0,
          currency: po.currency || 'USD'
        }
      })
    }
  }

  updateInvoiceFormData = (data: Partial<InvoiceFormData>) => {
    this.invoiceFormData = { ...this.invoiceFormData, ...data }
    this.isDirty = true
  }

  setSelectedApprovers = (approverIds: string[]) => {
    this.selectedApproverIds = approverIds
  }

  setSelectedLineItems = (lineItemIds: string[]) => {
    this.selectedLineItemIds = lineItemIds
    this.isDirty = true
  }

  addValidationError = (field: string, message: string) => {
    runInAction(() => {
      this.validationErrors.set(field, message)
    })
  }

  clearValidationErrors = () => {
    runInAction(() => {
      this.validationErrors.clear()
    })
  }


  // Async actions
  checkConversion = async () => {
    if (!this.selectedPOId) return

    runInAction(() => {
      this.isCheckingConversion = true
      this.clearValidationErrors()
    })

    try {
      const foundPO = (this.rootStore as any).purchaseOrdersStore.entities.find(
        (po: any) => po.purchaseOrderId === this.selectedPOId || po.id === this.selectedPOId
      )

      // Check if PO is already converted
      if (foundPO?.convertedToInvoice) {
        runInAction(() => {
          this.conversionCheckResult = {
            canConvert: false,
            reason: 'This purchase order has already been converted to an invoice',
            po: foundPO
          }
          this.isCheckingConversion = false
        })
        return
      }

      // Check if PO has line items
      const lineItems = (this.rootStore as any).purchaseOrderLineItemsStore?.entities || []
      const poId = foundPO?.purchaseOrderId || foundPO?.id
      const hasLineItems = lineItems.some((item: any) =>
        String(item.purchaseOrderId || item.poId) === String(poId) ||
        (item.purchaseOrderId || item.poId) === Number(poId)
      )

      runInAction(() => {
        if (!foundPO) {
          this.conversionCheckResult = {
            canConvert: false,
            reason: 'Purchase order not found'
          }
        } else if (!hasLineItems) {
          this.conversionCheckResult = {
            canConvert: false,
            reason: 'Purchase order has no line items',
            po: foundPO
          }
        } else {
          this.conversionCheckResult = {
            canConvert: true,
            po: foundPO
          }
        }
        this.isCheckingConversion = false
      })
    } catch (error: any) {
      runInAction(() => {
        this.conversionCheckResult = {
          canConvert: false,
          reason: error.message || 'Failed to validate purchase order'
        }
        this.addValidationError('conversion', error.message || 'Failed to validate purchase order')
        this.isCheckingConversion = false
      })
    }
  }

  convertToInvoice = async () => {
    if (!this.validateConversion()) {
      this.addValidationError('form', 'Please complete all required fields before converting')
      return false
    }

    runInAction(() => {
      this.isSubmitting = true
      this.clearValidationErrors()
      this.conversionSuccess = false
    })

    try {
      // Create invoice directly using the invoices service
      const invoicesStore = (this.rootStore as any).invoicesStore

      if (!invoicesStore) {
        throw new Error('Invoices service not available')
      }

      // Prepare invoice data from PO and form data
      const po = this.selectedPO

      // Ensure we have a customer ID
      const customerId = this.invoiceFormData.customerId || po?.accountId || po?.customerId || po?.supplierId
      if (!customerId) {
        throw new Error('Customer ID is required to create an invoice')
      }

      // Format dates as YYYY-MM-DD strings as required by backend
      const formatDate = (date: Date | undefined): string => {
        const d = date || new Date()
        const year = d.getFullYear()
        const month = String(d.getMonth() + 1).padStart(2, '0')
        const day = String(d.getDate()).padStart(2, '0')
        return `${year}-${month}-${day}`
      }

      const invoiceDate = formatDate(this.invoiceFormData.invoiceDate)
      const dueDate = formatDate(this.invoiceFormData.dueDate)

      // Calculate totals based on selected line items
      let subtotal = this.invoiceFormData.totalAmount || 0
      if (this.selectedLineItemIds.length > 0) {
        const lineItems = (this.rootStore as any).purchaseOrderLineItemsStore?.entities || []
        subtotal = lineItems
          .filter((item: any) => this.selectedLineItemIds.includes(item.lineItemId || item.id))
          .reduce((sum: number, item: any) => sum + ((item.quantity || 0) * (item.unitPrice || 0)), 0)
      }

      // Ensure subtotal is a valid number
      subtotal = Math.round(subtotal * 100) / 100

      const taxAmount = Math.round((subtotal * (this.invoiceFormData.taxRate || 0)) / 100 * 100) / 100
      const totalAmount = Math.round((subtotal + taxAmount) * 100) / 100

      // Create invoice data matching backend requirements exactly
      const invoiceData = {
        // Required fields
        invoiceNumber: this.invoiceFormData.invoiceNumber,
        customerId: Number(customerId), // Ensure it's a number
        invoiceDate: invoiceDate,  // Format: YYYY-MM-DD
        dueDate: dueDate,          // Format: YYYY-MM-DD
        subtotal: subtotal,
        taxAmount: taxAmount,
        totalAmount: totalAmount,
        status: 'draft' as const,  // Required enum value
        
        // Optional but useful fields
        paymentTerms: Number(this.invoiceFormData.paymentTerms) || 30, // Must be a number
        poNumber: this.invoiceFormData.poNumber || po?.poNumber || undefined,
        purchaseOrderId: this.selectedPOId ? Number(this.selectedPOId) : undefined, // Number if creating from PO
        billingAddress: this.invoiceFormData.billingAddress || '',
        shippingAddress: this.invoiceFormData.shippingAddress || '',
        notes: this.invoiceFormData.notes || '',
        taxRate: Number(this.invoiceFormData.taxRate) || 0, // 0-100 range
        
        // Use correct field names per backend requirements
        amountDue: totalAmount, // NOT balanceDue
        amountPaid: 0, // NOT paidAmount
        
        // Additional fields that may be needed
        currency: this.invoiceFormData.currency || 'USD'
        
        // Do NOT send: customerCode, paidAmount, balanceDue, terms
      }

      // Create the invoice
      const invoice = await invoicesStore.create(invoiceData)

      // The created invoice is automatically added to the store by the ServiceStore
      const invoiceIdToNavigate = invoice.invoiceId || invoice.id

      runInAction(() => {
        this.isSubmitting = false
        this.isDirty = false
        this.conversionSuccess = true

        // Update PO to mark it as converted
        if ((this.rootStore as any).purchaseOrdersStore) {
          (this.rootStore as any).purchaseOrdersStore.patch(this.selectedPOId!, {
            convertedToInvoice: true,
            invoiceId: invoiceIdToNavigate,
            invoiceNumber: invoice.invoiceNumber
          }).catch(() => {
            // Don't fail the whole operation if PO update fails
          })
        }
      })

      // Navigate to the created invoice after a short delay to show success
      setTimeout(() => {
        this.rootStore.routerStore.goTo('InvoiceDetail', {
          invoiceId: invoiceIdToNavigate
        })
      }, 1500)

      return true
    } catch (error: any) {
      runInAction(() => {
        this.isSubmitting = false
        this.handleConversionError(error)
      })
      return false
    }
  }

  handleConversionError = (error: any) => {
    // Parse error details
    const errorMessage = error.message || error.data?.message || 'Unknown error'
    const errorCode = error.code || error.status

    if (errorCode === 409) {
      this.validationErrors.set('conversion', 'This PO has already been converted to an invoice')
      this.serviceHasError = true
      this.errorMessage = 'This purchase order has already been converted'
    } else if (errorCode === 400) {
      // Parse validation errors if present
      if (error.data?.errors) {
        Object.entries(error.data.errors).forEach(([field, message]) => {
          this.validationErrors.set(field, String(message))
        })
      } else {
        this.validationErrors.set('conversion', errorMessage)
      }
      this.serviceHasError = true
      this.errorMessage = 'Please check the form for errors'
    } else if (errorCode === 404) {
      this.validationErrors.set('conversion', 'Purchase order not found')
      this.serviceHasError = true
      this.errorMessage = 'The selected purchase order could not be found'
    } else if (errorCode === 500 || errorCode === 503) {
      this.validationErrors.set('conversion', 'Server error. Please try again later.')
      this.serviceHasError = true
      this.errorMessage = 'A server error occurred. Please try again later.'
    } else {
      this.validationErrors.set('conversion', errorMessage)
      this.serviceHasError = true
      this.errorMessage = errorMessage
    }
  }

  saveDraft = async () => {
    this.isSaving = true

    try {
      const draftData = {
        purchaseOrderId: this.selectedPOId,
        invoiceFormData: this.invoiceFormData,
        approverIds: this.selectedApproverIds,
        currentStep: this.currentStep
      }

      // Only save draft if the service exists
      if ((this.rootStore as any).conversionDraftsStore) {
        await (this.rootStore as any).conversionDraftsStore.create(draftData)
      }

      runInAction(() => {
        this.isSaving = false
        this.isDirty = false
      })
    } catch (error) {
      runInAction(() => {
        this.isSaving = false
      })
    }
  }

  // Computed values - Reference ServiceStore entities
  get selectedPO() {
    if (!this.selectedPOId) return null
    return (this.rootStore as any).purchaseOrdersStore.entities.find((po: any) =>
      po.purchaseOrderId === this.selectedPOId || po.id === this.selectedPOId
    )
  }

  // Preview dialog computed properties
  get previewPOId() {
    if (!this.rootStore.routerStore?.queryParams?.dialogContext) return null
    return this.rootStore.routerStore.queryParams.dialogContext
  }

  get previewPO() {
    const poId = this.previewPOId
    if (!poId) return null

    const purchaseOrdersStore = (this.rootStore as any).purchaseOrdersStore
    return purchaseOrdersStore.entities.find((p: any) =>
      p.purchaseOrderId === poId || p.id === poId ||
      String(p.purchaseOrderId) === String(poId) || String(p.id) === String(poId)
    )
  }

  get previewLineItems() {
    const poId = this.previewPOId
    if (!poId) return []

    const purchaseOrderLineItemsStore = (this.rootStore as any).purchaseOrderLineItemsStore || (this.rootStore as any).lineItemsStore
    if (!purchaseOrderLineItemsStore) {
      // Fallback to line items from PO if available
      return this.previewPO?.lineItems || []
    }

    return purchaseOrderLineItemsStore.entities.filter(
      (item: any) => item.purchaseOrderId === poId ||
        String(item.purchaseOrderId) === String(poId)
    )
  }

  get convertiblePOs() {
    const purchaseOrders = (this.rootStore as any).purchaseOrdersStore.entities || []
    const lineItems = (this.rootStore as any).purchaseOrderLineItemsStore?.entities || []

    // Filter POs that have at least one line item
    return purchaseOrders.filter((po: any) => {
      const poId = po.purchaseOrderId || po.id
      const hasLineItems = lineItems.some((item: any) =>
        item.purchaseOrderId === poId
      )
      // Also check if not already converted to invoice
      const notConverted = !po.convertedToInvoice
      return hasLineItems && notConverted
    })
  }

  get availableApprovers() {
    return (this.rootStore as any).userStore?.entities
      .filter((user: any) => user.permissions?.includes('approve_invoices')) || []
  }

  get requiresApproval() {
    const amount = this.invoiceFormData.totalAmount || 0
    return amount > 10000 // Business rule example
  }

  get canProceedToNext() {
    switch (this.currentStep) {
      case 0: return !!this.selectedPOId && this.conversionCheckResult?.canConvert
      case 1: return this.isInvoiceDetailsValid
      case 2: return true // Line items optional
      case 3: return !this.requiresApproval || this.selectedApproverIds.length > 0
      case 4: return this.isConversionValid
      default: return false
    }
  }

  get isInvoiceDetailsValid() {
    return !!this.invoiceFormData.invoiceNumber &&
      !!this.invoiceFormData.invoiceDate &&
      !!this.invoiceFormData.dueDate
  }

  get isConversionValid() {
    return !!this.selectedPOId &&
      this.isInvoiceDetailsValid &&
      (!this.requiresApproval || this.selectedApproverIds.length > 0)
  }

  // Validation methods
  validateInvoiceDetails = () => {
    runInAction(() => {
      this.clearValidationErrors()

      if (!this.invoiceFormData.invoiceNumber) {
        this.validationErrors.set('invoiceNumber', 'Invoice number is required')
      }

      if (!this.invoiceFormData.invoiceDate) {
        this.validationErrors.set('invoiceDate', 'Invoice date is required')
      }

      if (!this.invoiceFormData.dueDate) {
        this.validationErrors.set('dueDate', 'Due date is required')
      }
    })

    return this.validationErrors.size === 0
  }

  validateConversion = () => {
    return this.selectedPOId &&
      this.isInvoiceDetailsValid &&
      (!this.requiresApproval || this.selectedApproverIds.length > 0)
  }

  // Cleanup
  clearErrors = () => {
    this.serviceHasError = false
    this.errorMessage = null
    this.validationErrors.clear()
  }

  reset = () => {
    this.currentStep = 0
    this.selectedPOId = null
    this.invoiceFormData = {}
    this.selectedApproverIds = []
    this.selectedLineItemIds = []
    this.validationErrors.clear()
    this.isDirty = false
    this.conversionCheckResult = null
    this.serviceHasError = false
    this.errorMessage = null
    this.isCheckingConversion = false
    this.conversionSuccess = false
  }
}